/**
 * Install helpers for the Eli & Mag PWA.
 *
 * - Captures the browser's `beforeinstallprompt` event at boot so we can
 *   trigger the native install prompt on-demand later (Android / desktop Chromium).
 * - Detects iOS Safari (which has no native install prompt — users must use
 *   Share → Add to Home Screen).
 * - Detects if the app is already running as an installed PWA.
 * - Persists the user\'s decision (installed / later / dismissed) so we don\'t
 *   pester them on every visit.
 */

const DECISION_KEY = "em_install_decision"; // 'installed' | 'later' | 'dismissed' | 'unsupported' | null
const LAST_PROMPT_KEY = "em_install_prompted_at";

let deferredPrompt = null;
const listeners = new Set();

function emit() {
  listeners.forEach((l) => {
    try { l(); } catch { /* ignore */ }
  });
}

/** Call once at app boot to wire the global listeners. */
export function initInstallCapture() {
  if (typeof window === "undefined" || window.__em_install_wired) return;
  window.__em_install_wired = true;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Stop Chrome from showing its own mini-infobar so we can drive the flow.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    localStorage.setItem(DECISION_KEY, "installed");
    localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
    emit();
  });
}

export function onInstallStateChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIDevice = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = ua.includes("Macintosh") && "ontouchend" in document;
  return (isIDevice || isIPadOS) && !window.MSStream;
}

export function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // Exclude in-app browsers where A2HS isn\'t available (FB, IG, Line, etc.)
  if (/(FBAN|FBAV|Instagram|Line|Twitter|LinkedIn)/i.test(ua)) return false;
  // Chrome/Firefox on iOS use WebKit under the hood but their share flow is different.
  // We still route them through the tutorial — Add to Home works from Safari primarily.
  return true;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return Boolean(mm || iosStandalone);
}

export function hasDeferredPrompt() {
  return deferredPrompt !== null;
}

/** Fire the browser\'s native install prompt (Android / desktop Chromium). */
export async function triggerNativeInstall() {
  if (!deferredPrompt) return { ok: false, reason: "no-event" };
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emit();
    if (choice && choice.outcome === "accepted") {
      localStorage.setItem(DECISION_KEY, "installed");
      localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
      return { ok: true, outcome: "accepted" };
    }
    localStorage.setItem(DECISION_KEY, "dismissed");
    localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
    return { ok: false, outcome: "dismissed" };
  } catch (e) {
    return { ok: false, reason: "error", error: e };
  }
}

export function getInstallDecision() {
  try { return localStorage.getItem(DECISION_KEY); } catch { return null; }
}

export function setInstallDecision(v) {
  try {
    localStorage.setItem(DECISION_KEY, v);
    localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

/** True when the guest has finished with the install step for now
 *  (installed, dismissed, chose later, or lives on a platform we can\'t install on).
 *  Used by PushOptInModal to know when it may take the stage.
 */
export function isInstallStepSettled() {
  if (isStandalone()) return true;
  const d = getInstallDecision();
  return d === "installed" || d === "dismissed" || d === "later" || d === "unsupported";
}

/** Whether we should show the install modal now. */
export function shouldPromptInstall() {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false;
  const decision = getInstallDecision();
  if (decision === "installed" || decision === "dismissed") return false;
  // Give a soft cool-down for users who tapped 'later'.
  if (decision === "later") {
    const last = Number(localStorage.getItem(LAST_PROMPT_KEY) || 0);
    if (Date.now() - last < 7 * 24 * 60 * 60 * 1000) return false;
  }
  // Platform must support it (Android/desktop Chromium OR iOS Safari).
  if (isIOSSafari()) return true;
  // On non-iOS, we need either a deferred prompt OR wait a beat — the caller
  // decides how long to wait. We optimistically say yes.
  return true;
}

export function platform() {
  if (isIOSSafari()) return "ios";
  if (typeof window !== "undefined" && /Android/i.test(navigator.userAgent)) return "android";
  return "other";
}
